import { getFedExOptIn } from '@shared/api/fedEx.js'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, watch } from 'vue'
import useVuexModule from '@shared/composables/vuexModule.js'
import { useOnboardingStore } from '@/stores/onboarding.js'
import apiClient from '@/store/api/apiClient.js'
import { until } from '@vueuse/core'

// TODO: Partial implementation (ENG-2846)
export const useClientStore = defineStore('client', () => {
  const vuex = useVuexModule('client', {
    defaults: {
      state: [
        ['loaded', false],
        'impersonator',
        'profilePic',
        ['firstName', ''],
        ['lastName', ''],
        ['isImpersonated', false],
        ['isAuthenticated', false],
        ['username', ''],
        ['email', ''],
        'authToken',
        ['membershipStatus', ''],
        'selectedPackages',
        'styleProfileId',
        ['returnCarrierPreference', 'FEDEX'],
        'shippingAddress',
        'pickupAddress',
        ['showFedexSuccessModal', false],
        ['showUnlimitedSwapsUpgrade', true],
        ['membershipDetails', null],
        ['returnPackages', []],
        ['paidThrough', ''],
        ['clientIsPrepay', false],
        ['settings', {}],
        ['plans', {}],
      ],
      getters: [
        ['active', () => false],
        'paymentIsExpired',
        'showFirstCaseCountdown',
      ],
      actions: [
        'getClient'
      ]
    }
  })

  const {
    impersonator,
    profilePic,
    firstName,
    lastName,
    membershipDetails,
    membershipStatus,
    plans,
    selectedPackages,
    showFedexSuccessModal,
    returnCarrierPreference,
    returnPackages,
    settings
  } = vuex

  const completedUnlimitedIntro = computed(() => settings.value?.onboarding?.completedUnlimitedIntro ?? false)
  const shownHowToSwap = computed(() => settings.value?.onboarding?.shownHowToSwap ?? false)

  const currentBasePlan = computed(() => plans.value?.currentBasePlan)
  const unlimitedSubscriber = computed(() => currentBasePlan.value?.shipmentsPerInterval === null)

  const name = computed(() => [firstName.value, lastName.value].filter(Boolean).join(' '))
  const greetingName = computed(() => firstName.value?.length > 0 ? firstName.value : 'Boss Lady')
  const hasProfileImage = computed(() => profilePic.value?.length > 0)
  const hasCustomProfileImage = computed(() => hasProfileImage.value && !profilePic.value.includes('dresses'))
  const impersonatorName = computed(() => [impersonator.value?.first_name, impersonator.value?.last_name].filter(Boolean).join(' '))
  const impersonatorGroups = computed(() => impersonator.value?.groups)
  const impersonatorIsStylist = computed(() => impersonatorGroups.value?.includes('Remote Stylist') ?? false)
  const formerMember = computed(() => membershipStatus.value === 'former_member')
  const latestSubscriptionPlanId = computed(() => membershipDetails.value?.latestSubscriptionPlanId)
  const membershipStartDate = computed(() => {
    const start = membershipDetails.value?.membershipStart
    return start ? new Date(start) : null
  })
  const subscriptionStartDate = computed(() => {
    const start = membershipDetails.value?.subscriptionStart
    return start ? new Date(start) : null
  })
  const isFirstSubscription = computed(() =>
    membershipStartDate.value &&
    subscriptionStartDate.value &&
    membershipStartDate.value.getTime() === subscriptionStartDate.value.getTime())

  const availableSwaps = computed(() => membershipDetails.value?.allowedShipments)

  const joinedAfterOnboardingLaunch = computed(() => 
    membershipStartDate.value && membershipStartDate.value >= new Date('2025-06-23')
  )
  const onboardingEligible = computed(() =>
    membershipStatus.value === 'active' &&
    joinedAfterOnboardingLaunch.value &&
    selectedPackages.value < 2
  )

  async function fedExOptIn () {
    await getFedExOptIn()
    returnCarrierPreference.value = 'FEDEX'
    showFedexSuccessModal.value = true
  }

  async function getReturnPackages (count) {
    const res = await apiClient.getReturnPackages(count)
    returnPackages.value = res.data
  }

  const styleQuizNav = useVuexModule('styleQuizNav')
  watch(onboardingEligible, (show) => {
    styleQuizNav.SET_STYLE_QUIZ_ROUTE_DISPLAYED({ name: 'style-quiz-success', display: !show })
    const { triggerOnboardingFlow, dismissOnboardingFlow } = useOnboardingStore()
    if (show) {
      triggerOnboardingFlow()
    } else {
      dismissOnboardingFlow()
    }
  })

  async function untilClientLoaded({ timeout = 2000, throwOnTimeout = true } = {}) {
    if (!vuex.loaded.value) {
      await Promise.all([
        vuex.getClient().catch((error) => {
          if (throwOnTimeout) {
            throw error
          }
        }),
        until(vuex.loaded).toBe(true, { timeout, throwOnTimeout })
      ])
    }
  }

  return {
    ...vuex,
    name,
    greetingName,
    hasProfileImage,
    hasCustomProfileImage,
    impersonatorName,
    impersonatorGroups,
    impersonatorIsStylist,
    formerMember,
    latestSubscriptionPlanId,
    membershipStartDate,
    subscriptionStartDate,
    isFirstSubscription,
    joinedAfterOnboardingLaunch,
    onboardingEligible,
    completedUnlimitedIntro,
    shownHowToSwap,
    currentBasePlan,
    unlimitedSubscriber,
    availableSwaps,
    fedExOptIn,
    getReturnPackages,
    untilClientLoaded,
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useClientStore, import.meta.hot))
